import type React from 'react'
import type { LinkableTextFrame } from '@/lib/text-linking/types/linkable-text-frame'
import {
  type PortPoint,
  connectorPathD,
  outPortPoint,
} from '@/lib/text-linking/text-linking-geometry'

import { PORT_STROKE_COLOR } from '../constants/port-style'
import { chromeFrameOf } from '../helpers/chrome-frame-of'

/**
 * Rubber-band preview while linking. `cursorDocPoint` is exactly that — document space, as
 * tracked by the pointer-move handler in center-canvas/hooks/use-text-link-drag.ts — so it's
 * projected through `scale` here, at the one place this layer still consumes a doc point directly.
 */
export function LinkingRubberBand({
  sourceId,
  cursorDocPoint,
  allTextFrames,
  scale,
}: {
  sourceId: string
  cursorDocPoint: PortPoint
  allTextFrames: LinkableTextFrame[]
  scale: number
}): React.JSX.Element | null {
  const source = allTextFrames.find((f) => f.id === sourceId)

  if (!source) return null
  const sourceChrome = chromeFrameOf(source, scale)
  const from = outPortPoint(sourceChrome, undefined, sourceChrome.pivot)
  const to = { x: cursorDocPoint.x * scale, y: cursorDocPoint.y * scale }

  return (
    <path
      d={connectorPathD(from, to)}
      fill="none"
      stroke={PORT_STROKE_COLOR}
      strokeWidth={1.5}
      strokeDasharray="3 3"
      aria-hidden="true"
    />
  )
}
